import { FormLabel, Grid, XDatePicker } from '@freighthub/silhouette-core'
import { addDays } from 'date-fns'
import {
  AddressType,
  BookingParty,
  GenericLocationType,
  getDestinationPortType,
  getOriginPortType,
  IncotermSelector,
  QRAutocomplete,
} from 'features/BookingFlow'
import { isAddress, isLocation, PlacesSearchType, placeToString } from 'features/Directory'
import { ShipmentMode, ShipmentType } from 'features/ShipmentDetails'
import { FC, ReactNode, useMemo } from 'react'
import { useController, useFormState } from 'react-hook-form'
import { useI18n } from 'utils/localisation'
import { z } from 'zod'

import { AdditionalRequirements, MQFields } from './MQFormContentProcessor'

type MQFormFieldProps = {
  label: ReactNode
  field: ReactNode
  optional?: boolean
}
const INCOTERMS_URL = 'https://go.freighthub.com/incoterms_2020'
const maxCargoReadyDate = addDays(new Date(), 41)
const MQFormField: FC<MQFormFieldProps> = ({ label, field, optional = false }) => {
  return (
    <>
      <Grid xs={3}>
        <FormLabel required={!optional}>{label}</FormLabel>
      </Grid>
      <Grid xs={9}>{field}</Grid>
    </>
  )
}

type MQFormCommonFieldsProps = {
  bookingParty: BookingParty
  transportModes: ShipmentMode[]
  transportType: ShipmentType
  crdHelperText?: string
}

const mqAdditionalRequirementsSchema = z.object({
  isHeavyTestedContainer: z.boolean().optional(),
  letterOfCreditRequired: z.boolean().optional(),
  maxPayloadContainer: z.string().optional(),
  maxDemurrageTime: z.string().optional(),
  EXWDate: z.string().optional(),
  transportToPortMode: z.string().optional(),
  transportCompanyPreference: z.string().optional(),
}) as z.ZodType<AdditionalRequirements>

export const MQCommonFieldsFormSchema = z.object({
  cargoReadyAt: z.date(),
  incoTerm: z.string(),
  pickupAddress: z.string().optional(),
  deliveryAddress: z.string().optional(),
  additionalRequirements: mqAdditionalRequirementsSchema.optional(),
})

const placeSelectionToString = (location: GenericLocationType): string => {
  return (isLocation(location) ? placeToString(location.place) : placeToString(location)) as string
}
export const MQFormCommonFields: FC<MQFormCommonFieldsProps> = ({
  bookingParty,
  transportModes,
  transportType,
  crdHelperText,
}) => {
  const { translate } = useI18n()
  const { errors } = useFormState<MQFields>()
  const { field: cargoReadyAtField } = useController<MQFields, 'cargoReadyAt'>({ name: 'cargoReadyAt' })
  const { field: pickupAddressField } = useController<MQFields, 'pickupAddress'>({ name: 'pickupAddress' })
  const { field: pickupLocationField } = useController<MQFields, 'pickupLocation'>({ name: 'pickupLocation' })
  const { field: deliveryAddressField } = useController<MQFields, 'deliveryAddress'>({ name: 'deliveryAddress' })
  const { field: deliveryLocationField } = useController<MQFields, 'deliveryLocation'>({
    name: 'deliveryLocation',
  })
  const { field: incoTermField } = useController<MQFields, 'incoTerm'>({ name: 'incoTerm' })

  const onPickupSelection = (placeInput: GenericLocationType) => {
    pickupAddressField.onChange(placeSelectionToString(placeInput))
    pickupLocationField.onChange(placeInput)
  }

  const onDeliverySelection = (placeInput: GenericLocationType) => {
    deliveryAddressField.onChange(placeSelectionToString(placeInput))
    deliveryLocationField.onChange(placeInput)
  }

  const pickupTypes: PlacesSearchType[] = ['address', ...transportModes.map(getOriginPortType)]
  const deliveryTypes: PlacesSearchType[] = useMemo(() => {
    const isRailOnly = transportModes.length === 1 && transportModes[0] === 'rail'
    if (isRailOnly && transportType === 'fcl') {
      return ['address']
    }
    return ['address', ...transportModes.map(getDestinationPortType)]
  }, [transportModes, transportType])

  return (
    <>
      <MQFormField
        label={translate('manual_quote-pickup_address')}
        field={
          <QRAutocomplete
            placeholder={translate('manual_quote-address_placeholder')}
            onSelect={onPickupSelection}
            types={pickupTypes}
            filter="pa"
            value={pickupLocationField.value}
          />
        }
      />
      <MQFormField
        label={translate('manual_quote-delivery_address')}
        field={
          <QRAutocomplete
            placeholder={translate('manual_quote-address_placeholder')}
            onSelect={onDeliverySelection}
            types={deliveryTypes}
            filter="da"
            value={deliveryLocationField.value}
          />
        }
      />
      <MQFormField
        label={translate('manual_quote-cargo_ready_at-input')}
        field={
          <XDatePicker
            value={cargoReadyAtField.value}
            helperText={errors.cargoReadyAt?.message || crdHelperText}
            onChange={cargoReadyAtField.onChange}
            maxDate={maxCargoReadyDate}
            required
            disablePast
          />
        }
      />
      <MQFormField
        label={<a href={INCOTERMS_URL}>{translate('manual_quote_dialog-select_incoterm')}</a>}
        field={
          <IncotermSelector
            selectedIncoTerm={incoTermField.value}
            handleIncoTermChange={incoTermField.onChange}
            fullWidth
            required
            bookingParty={bookingParty}
            originType={isAddress(pickupLocationField.value) ? AddressType.Door : AddressType.Port}
            destinationType={isAddress(deliveryLocationField.value) ? AddressType.Door : AddressType.Port}
            transportType={transportType}
            transportModes={transportModes}
          />
        }
      />
    </>
  )
}
